import React, {RefObject} from 'react';
import {observer} from 'mobx-react';
import {configStore, cupController, cupStore} from 'app/store/global';
import {openAuth, openRegistration} from 'app/components/auth/auth';
import {getLinkPath} from 'app/utils';
import I18n from 'app/i18n';
import type {CupUserTokenPayload} from 'app/store/CupStore';
import {startSteamAuthFlow} from 'app/utils/steamLink';
import {LobbyAvailabilityWatcher} from 'app/utils/lobbyAvailability';

interface State {
    lobbyUnavailable: boolean
    reloadAttempt: number
}

interface CupMessage {
    type?: 'cup:request-user-token' | 'cup:require-login' | 'cup:route' | 'cup:resize' |
        'cup:scroll-to-top' | 'cup:link-steam'
    hash?: string
    height?: number
    tab?: 'login' | 'register'
}

const ISO_DATE_LENGTH = 10;
const MIN_LOBBY_HEIGHT_PX = 720;

@observer
class EgbCup extends React.Component<unknown, State> {
    iframeRef: RefObject<HTMLIFrameElement> = React.createRef();

    private readonly lobbyWatcher: LobbyAvailabilityWatcher;

    constructor(props: unknown) {
        super(props);

        this.state = {lobbyUnavailable: false, reloadAttempt: 0};
        this.lobbyWatcher = new LobbyAvailabilityWatcher({
            embedded: false,
            getLobbyOrigin: () => cupStore.lobbyOrigin,
            lobby: 'cup',
            onRecovered: this.handleLobbyRecovered,
            onUnavailable: this.handleLobbyUnavailable
        });
    }

    componentDidMount(): void {
        window.addEventListener('message', this.handleMessage);
        window.addEventListener('popstate', this.handlePopState);
        this.lobbyWatcher.start();
    }

    componentWillUnmount(): void {
        window.removeEventListener('message', this.handleMessage);
        window.removeEventListener('popstate', this.handlePopState);
        this.lobbyWatcher.stop();
    }

    buildIframeSrc = (): string => {
        const params = new URLSearchParams({
            _v: new Date().toISOString().slice(0, ISO_DATE_LENGTH),
            balance: cupStore.balance,
            centrifugoUrl: cupStore.centrifugoPath,
            countryCode: cupStore.countryCode,
            currency: cupStore.currency,
            email: cupStore.email,
            embeddedInWidget: 'true',
            externalId: cupStore.currentExternalId,
            lang: cupStore.lang,
            nickname: cupStore.nickname,
            parentOrigin: cupStore.parentOrigin,
            steamId64: cupStore.steamId64,
            timezone: cupStore.timezone,
            userToken: cupStore.userToken
        });

        if (cupStore.langExplicit) {
            params.set('langExplicit', '1');
        }

        return `${cupStore.lobbyUrl}?${params.toString()}${window.location.hash || ''}`;
    };

    postUserToken = ({userToken, nickname, countryCode, email, balance, currency, steamId64}: CupUserTokenPayload): void => {
        this.iframeRef.current?.contentWindow?.postMessage(
            {balance, countryCode, currency, email, nickname, steamId64, type: 'cup:user-token', userToken},
            cupStore.lobbyOrigin
        );
    };

    requestUserTokenRefresh = (): void => {
        cupController.refreshUserToken()
            .then(this.postUserToken)
            .catch(() => null);
    };

    currentCupPath = (): string => getLinkPath(`/cup${window.location.search}${window.location.hash || ''}`);

    handleMessage = (event: MessageEvent<CupMessage>): void => {
        if (event.source !== this.iframeRef.current?.contentWindow) {
            return;
        }

        this.lobbyWatcher.markAlive();

        const {data} = event;

        switch (data?.type) {
        case 'cup:request-user-token':
            this.requestUserTokenRefresh();
            break;
        case 'cup:require-login':
            if (data.tab === 'register') {
                openRegistration('CUP', this.currentCupPath());
            } else {
                openAuth(this.currentCupPath());
            }
            break;
        case 'cup:route':
            if (typeof data.hash === 'string') {
                history.replaceState(null, '', getLinkPath(`/cup${window.location.search}${data.hash}`));
            }
            break;
        case 'cup:link-steam':
            startSteamAuthFlow();
            break;
        case 'cup:resize':
            if (typeof data.height === 'number' && data.height > 0) {
                const iframe = this.iframeRef.current;

                if (iframe) {
                    iframe.style.height = `${Math.max(data.height, MIN_LOBBY_HEIGHT_PX)}px`;
                }
            }
            break;
        case 'cup:scroll-to-top':
            window.scrollTo(0, 0);
            break;
        default:
            break;
        }
    };

    handlePopState = (): void => {
        this.iframeRef.current?.contentWindow?.postMessage(
            {hash: window.location.hash || '#/', type: 'cup:navigate'},
            cupStore.lobbyOrigin
        );
    };

    handleLobbyUnavailable = (): void => {
        this.setState({lobbyUnavailable: true});
    };

    handleLobbyRecovered = (): void => {
        this.setState({lobbyUnavailable: false});
    };

    handleRetry = (): void => {
        const reloadAttempt = this.lobbyWatcher.retry();

        this.setState({lobbyUnavailable: false, reloadAttempt});
    };

    handleIframeLoad = (): void => {
        this.lobbyWatcher.markIframeLoaded();
    };

    renderUnavailableStub(): React.ReactNode {
        return <div className="cup-lobby-card">
            <p className="cup-lobby-card__text">{I18n.t('cup.lobby_unavailable')}</p>
            <button
                className="cup-lobby-card__reload-button"
                onClick={this.handleRetry}
                type="button"
            >
                {I18n.t('cup.try_again')}
            </button>
        </div>;
    }

    render(): React.ReactNode {
        if (!configStore.isReady) {
            return null;
        }

        const {lobbyUnavailable, reloadAttempt} = this.state;
        const iframeClassName = lobbyUnavailable
            ? 'cup-page__lobby-iframe cup-page__lobby-iframe--hidden'
            : 'cup-page__lobby-iframe';

        return <div className="cup-page">
            <iframe
                allow="clipboard-write; fullscreen"
                allowFullScreen
                aria-hidden={lobbyUnavailable}
                className={iframeClassName}
                id="cup-lobby"
                key={`cup-lobby-${reloadAttempt}`}
                onLoad={this.handleIframeLoad}
                ref={this.iframeRef}
                scrolling="no"
                src={this.buildIframeSrc()}
                title="cup-lobby"
            />
            {lobbyUnavailable && this.renderUnavailableStub()}
        </div>;
    }
}

export default EgbCup;
